import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import Container from '/@/components/container';
import FieldviewLoginPopup from '/@/components/fieldview-login-popup';
import Card from '/@/components/new-home/card';
import Popup from '/@/components/popup';
import Terms from '/@/components/terms';
import settings from '/@/core/settings';
import { withRouter } from '/@/hooks/with-router';
import { _ } from '/@/locale';
import { checkInternal } from '/@/settings/menu.js';
import { getBagQualityEnabled } from '/@/store/bag-quality/selectors';
import { useCatalogZones } from '/@/store/catalog/apiProviders';
import { getCatalogEnabled } from '/@/store/catalog/selectors';
import { getLibraryEnabled } from '/@/store/library/selectors';
import { getSoyMultipliersEnabled } from '/@/store/multipliers/selectors';
import { setSelectedFeature, setShowTermsAndConditions } from '/@/store/nav/actions';
import { getShowTermsAndConditions } from '/@/store/nav/selectors';
import { getWeedsRecommendationEnabled } from '/@/store/private-data/recommendation-weeds/selectors';
import {
    getAppInstance,
    getCornRecommendationEnabled,
    getMapsEnabled,
    getNitrogenRecommendationEnabled,
    getSoyRecommendationEnabled,
    getUser,
} from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking/actions';
import { getWeatherFeatureEnabled } from '/@/store/weather/selectors';
import { isPhone } from '/@/utils';
import { reverse } from '/@/utils/router';
import Title from '/@/v3/components/title';
import styles from './styles.module.css';

@connect(
    (state) => {
        const appInstance = getAppInstance(state);
        return {
            appInstance,
            user: getUser(state),
            bagQualityEnabled: getBagQualityEnabled(state),
            weatherEnabled: getWeatherFeatureEnabled(state),
            soyMultipliersEnabled: getSoyMultipliersEnabled(state),
            soyRecommendationEnabled: getSoyRecommendationEnabled(state, appInstance),
            cornRecommendationEnabled: getCornRecommendationEnabled(state, appInstance),
            nitrogenRecommendationEnabled: getNitrogenRecommendationEnabled(state),
            mapsEnabled: getMapsEnabled(state),
            offline: state.network.offline,
            libraryEnabled: getLibraryEnabled(state),
            weedsEnabled: getWeedsRecommendationEnabled(state),
            showTermsAndConditions: getShowTermsAndConditions(state),
        };
    },
    (dispatch) => bindActionCreators({ setSelectedFeature, trackEvent, setShowTermsAndConditions }, dispatch)
)
@withRouter
@autobind
class NewHome extends Component {
    constructor(props) {
        super(props);
        this.state = {
            provider: '',
        };
    }

    static contextTypes = {
        history: PropTypes.object,
    };

    componentDidMount() {
        const query = new URLSearchParams(this.props.location.search);
        const provider = query.get('provider');
        this.setState({ provider: provider });
        this.props.setSelectedFeature([]);
    }

    handleClickCard({ view, selectedFeature, crop }) {
        const data = { view, selectedFeature, crop };
        this.props.setSelectedFeature(data);
        this.context.history(reverse(view));
        const trackingKey = crop ? `${view}.${selectedFeature}.${crop}` : `${view}.${selectedFeature}`;
        this.props.trackEvent({ key: trackingKey.toLowerCase() });
    }

    renderTitle() {
        const { user, offline } = this.props;
        return (
            <div className={styles.containerTitle}>
                <Title className={styles.title} bolder color='primary' theme={settings.app.theme}>
                    {_('Hola, ')}
                    <span className='mp-mask'>{user.first_name}</span>
                </Title>
                <Title className={styles.subtitle}>
                    {_(
                        '¿Qué quisieras hacer ahora? \nTe ofrecemos estas opciones. Para ver más, podés acceder al menú.'
                    )}
                </Title>
                {offline && isPhone() && (
                    <div className={styles.containerOffline}>
                        <Title className={styles.subtitle}>{_('No disponible offline')}</Title>
                        <i className={classNames('material-icons', styles.iconOffline)}>wifi_off</i>
                    </div>
                )}
            </div>
        );
    }

    renderTerms() {
        return (
            <Popup
                onClose={() => this.props.setShowTermsAndConditions(false)}
                showCloseIcon
                visible={Boolean(this.props.showTermsAndConditions)}
                className={styles.popup}
            >
                <Terms />
            </Popup>
        );
    }

    renderRecomendations() {
        const { bagQualityEnabled, appInstance, soyRecommendationEnabled, cornRecommendationEnabled, offline } =
            this.props;
        const valueForCard = isPhone() ? 'row' : 'column';

        const isExpertoBayer = settings.app.theme === 'expertobayer';

        return (
            <div className={styles.containerCards}>
                <div className={styles.containerMapCards}>
                    <Card
                        icon='fields'
                        name={_('Mis Lotes')}
                        direction={valueForCard}
                        bolder
                        color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'green-light'}
                        offline={offline}
                        handleClick={() => this.handleClickCard({ view: 'Home', selectedFeature: 'FieldDetail' })}
                    />
                    <Card
                        icon='newField'
                        name={_('Nuevo Lote')}
                        direction={valueForCard}
                        color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'green-light'}
                        offline={offline}
                        bolder
                        handleClick={() =>
                            this.handleClickCard({ view: 'fields.NewField', selectedFeature: 'FieldDetail' })
                        }
                    />
                    {cornRecommendationEnabled && (
                        <Card
                            icon='corn'
                            name={_('Recomendación de Maíz')}
                            direction={valueForCard}
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'orange'}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({ view: 'Home', selectedFeature: 'recommendation', crop: 'corn' })
                            }
                            offline={offline}
                        />
                    )}
                    {soyRecommendationEnabled && (
                        <Card
                            icon='soy'
                            name={_('Recomendación de Soja')}
                            direction={valueForCard}
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'green'}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({ view: 'Home', selectedFeature: 'recommendation', crop: 'soy' })
                            }
                            offline={offline}
                        />
                    )}
                    {appInstance.enableCropMonitoring && (
                        <Card
                            icon='cropMonitoring'
                            name={_('Seguimiento de cultivo')}
                            direction={valueForCard}
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'orange'}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({
                                    view: 'cropMonitoring.CropMonitoring',
                                    selectedFeature: 'cropMonitoring',
                                })
                            }
                            offline={offline}
                        />
                    )}
                    {bagQualityEnabled && (
                        <Card
                            icon='techBag'
                            name={_('Calidad de Semilla Maíz')}
                            direction={valueForCard}
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'orange'}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({ view: 'BagQuality', selectedFeature: 'bagQuality' })
                            }
                            offline={offline}
                        />
                    )}
                    {appInstance.enableWeedFinder && (
                        <Card
                            icon='weeds'
                            name={_('Manejo de malezas')}
                            direction={valueForCard}
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'red'}
                            offline={offline}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({ view: 'Home', selectedFeature: 'weeds', crop: 'corn' })
                            }
                        />
                    )}
                    {appInstance.enableNitogrenRecommendation && (
                        <Card
                            icon='nitrogen'
                            name={_('Nitrógeno Smart')}
                            direction={valueForCard}
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'orange'}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({
                                    view: 'nitrogenRecommendation.NitrogenRecommendationList',
                                    selectedFeature: 'nitrogen',
                                })
                            }
                            offline={offline}
                        />
                    )}
                </div>
            </div>
        );
    }

    renderTools() {
        const {
            catalogEnabled,
            weatherEnabled,
            appInstance,
            user,
            soyMultipliersEnabled,
            mapsEnabled,
            offline,
            libraryEnabled,
        } = this.props;

        const isExpertoBayer = settings.app.theme === 'expertobayer';

        return (
            <div className={styles.containerCards}>
                <Title className={styles.titleCards} bolder>
                    {_('Herramientas')}
                </Title>
                <div className={styles.containerMapCards}>
                    {appInstance.enableCornHeadToHead && checkInternal(user, appInstance) && (
                        <Card
                            icon='corn'
                            name={_('Head to head Maíz')}
                            direction='row'
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'orange'}
                            offline={offline}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({ view: 'Home', selectedFeature: 'h2h', crop: 'corn' })
                            }
                        />
                    )}
                    {appInstance.enableSoyHeadToHead && checkInternal(user, appInstance) && (
                        <Card
                            icon='soy'
                            name={_('Head to head Soja')}
                            direction='row'
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'green'}
                            offline={offline}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({ view: 'Home', selectedFeature: 'h2h', crop: 'soy' })
                            }
                        />
                    )}
                    {checkInternal(user, appInstance) && appInstance.enableFieldTrialNetwork && (
                        <Card
                            icon='red'
                            name={_('Red de ensayos')}
                            direction='row'
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'blue'}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({
                                    view: 'FieldTrialNetwork',
                                    selectedFeature: 'fieldTrialNetwork',
                                })
                            }
                            offline={offline}
                        />
                    )}
                    {weatherEnabled && (
                        <Card
                            icon='weather'
                            name={_('Clima')}
                            direction='row'
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'blue'}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({
                                    view: 'Home',
                                    selectedFeature: isPhone() ? 'weatherPhone' : 'weather',
                                })
                            }
                            offline={offline}
                        />
                    )}
                    {catalogEnabled && (
                        <Card
                            icon='catalogue'
                            name={_('Catálogo')}
                            direction='row'
                            color={isExpertoBayer ? 'expertobayer' : 'blue'}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({ view: 'catalog.BrandSelection', selectedFeature: 'catalog' })
                            }
                            offline={offline}
                        />
                    )}
                    {libraryEnabled && (
                        <Card
                            icon='library'
                            name={_('Biblioteca')}
                            direction='row'
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'blue'}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({ view: 'library.LibraryList', selectedFeature: 'library' })
                            }
                            offline={offline}
                        />
                    )}
                    {mapsEnabled && (
                        <Card
                            icon='maps'
                            name={_('Mapas')}
                            direction='row'
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'blue'}
                            bolder
                            handleClick={() => this.handleClickCard({ view: 'Map', selectedFeature: 'maps' })}
                            offline={offline}
                        />
                    )}
                    {soyMultipliersEnabled && (
                        <Card
                            icon='ptv'
                            name={_('PTV Multiplicadores')}
                            direction='row'
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'green'}
                            offline={offline}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({ view: 'SoyMultipliers', selectedFeature: 'multipliers' })
                            }
                        />
                    )}
                    {appInstance.enableCarbonCalculator && (
                        <Card
                            icon='carbon'
                            name={_('Simulador de rotaciones sustentables')}
                            direction='row'
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'blue'}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({
                                    view: 'Home',
                                    selectedFeature: 'carbonCalculator',
                                })
                            }
                            offline={offline}
                        />
                    )}
                    {appInstance.enableCarbonCalculatorBr && (
                        <Card
                            icon='carbon'
                            name={_('Simulador de rotaciones sustentables')}
                            direction='row'
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'blue'}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({
                                    view: 'Home',
                                    selectedFeature: 'carbonCalculatorBR',
                                })
                            }
                            offline={offline}
                        />
                    )}
                    {appInstance.enableProductDatasheet && checkInternal(user, appInstance) && (
                        <Card
                            icon='productDatasheet'
                            name={_('Ficha Técnica de Producto')}
                            direction='row'
                            color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'blue'}
                            offline={offline}
                            bolder
                            handleClick={() =>
                                this.handleClickCard({
                                    view: 'productDatasheet.ProductDatasheetBrandSelection',
                                    selectedFeature: 'productDatasheet',
                                })
                            }
                        />
                    )}
                    <Card
                        icon='myRecos'
                        name={_('Mis Recomendaciones')}
                        direction='row'
                        color={isExpertoBayer ? 'expertobayer' : offline ? 'gray' : 'blue'}
                        bolder
                        handleClick={() =>
                            this.handleClickCard({
                                view: 'MyRecommendations',
                                selectedFeature: 'my-recommendations',
                            })
                        }
                        offline={offline}
                    />
                    {appInstance.enableFrostRisk && (
                        <Card
                            icon='frost'
                            name={_('Riesgo de helada')}
                            direction='row'
                            color={isExpertoBayer ? 'expertobayer' : 'blue'}
                            bolder
                            offline={offline}
                            handleClick={() =>
                                this.handleClickCard({
                                    view: 'frost_risk',
                                    selectedFeature: 'frost_risk',
                                })
                            }
                        />
                    )}
                </div>
            </div>
        );
    }

    render() {
        return (
            <Container className={styles.wrapper} classNameRoot={styles.containerRoot}>
                {this.state.provider === 'fieldview' && (
                    <FieldviewLoginPopup
                        handleClose={() => {
                            this.setState({ provider: '' });
                        }}
                        open={this.state.provider === 'fieldview'}
                        message={_('Has iniciado sesión con tu cuenta de Fieldview')}
                    />
                )}
                {this.renderTitle()}
                {this.renderRecomendations()}
                {this.renderTools()}
                {this.renderTerms()}
            </Container>
        );
    }
}

const NewHomeWrapped = (props) => {
    const catalogZonesQuery = useCatalogZones({ page: 1, pageSize: 10000, crop: props.type, type: 'catalog' });
    const catalogZones = useMemo(() => catalogZonesQuery.data?.results || [], [catalogZonesQuery.data?.results]);

    const catalogEnabled = useSelector((state) => getCatalogEnabled(state, catalogZones));
    return <NewHome {...props} catalogEnabled={catalogEnabled} />;
};

export default NewHomeWrapped;
